import React, { useState, useRef, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useTimer } from '../../context/TimerContext';
import StartTimerDropdown from './StartTimerDropdown';
import TimerCompletionModal from './TimerCompletionModal';
import { useTheme } from '../../context/ThemeContext';

// Format seconds to HH:MM:SS
const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Clock time (HH:mm) the timer was started at
const formatClock = (value?: string): string => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'HH:mm');
  } catch {
    return '';
  }
};

const TimerWidget: React.FC = () => {
  const { t } = useTranslation();
  const {
    activeTimer,
    isLoading,
    elapsedSeconds,
    showCompletionModal,
    stopTimer,
    openCompletionModal,
    closeCompletionModal,
  } = useTimer();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isDark } = useTheme();
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStop = async () => {
    try {
      await stopTimer();
    } catch (error) {
      console.error('Failed to stop timer:', error);
    }
  };

  // No active timer - show start button
  if (!activeTimer) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isLoading}
          className="flex items-center px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all duration-200 border border-transparent hover:border-slate-600"
        >
          <svg
            className="w-6 h-6 mr-2 text-green-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span
            className={`hidden sm:inline ${isDark ? 'text-slate-200' : 'text-slate-500'}`}
          >
            {t('timer.startTimer')}
          </span>
        </button>

        {showDropdown && (
          <StartTimerDropdown onClose={() => setShowDropdown(false)} />
        )}
      </div>
    );
  }

  // Timer is stopped but not converted - show "Complete Entry" button
  if (activeTimer.status === 'stopped') {
    return (
      <>
        <button
          onClick={openCompletionModal}
          className="flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-200"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="hidden sm:inline">{t('timer.completeEntry')}</span>
          <span className="sm:hidden">{formatTime(elapsedSeconds)}</span>
        </button>

        {showCompletionModal && (
          <TimerCompletionModal onClose={closeCompletionModal} />
        )}
      </>
    );
  }

  // Timer is running - show elapsed time and stop button
  return (
    <>
      <div className="relative flex items-center gap-2" ref={dropdownRef}>
        {/* Stop — the start button becomes a stop while a timer runs */}
        <button
          onClick={handleStop}
          disabled={isLoading}
          title={t('timer.stopTimer')}
          aria-label={t('timer.stopTimer')}
          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 border border-red-500/30 transition-all duration-200 disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="1" />
          </svg>
        </button>

        {/* Timer display — click to open the details box (project/task/tags) */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          title={t('timer.editDetails', { defaultValue: 'Edit timer details' })}
          className="flex items-center px-3 py-2 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 hover:border-green-400/60 transition-colors"
        >
          {/* Pulsing dot */}
          <span className="relative flex h-2 w-2 mr-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>

          {/* Time display */}
          <span className="font-mono text-sm font-medium text-green-400">
            {formatTime(elapsedSeconds)}
          </span>

          {/* Clock time the timer started at */}
          {formatClock(activeTimer.started_at) && (
            <span className="hidden sm:inline ml-2 font-mono text-xs text-slate-400">
              {formatClock(activeTimer.started_at)}
            </span>
          )}

          {/* Project · task - hidden on small screens */}
          <span className="hidden md:inline-flex ml-2 text-xs text-slate-400 max-w-[180px] truncate">
            {activeTimer.project?.name ||
              t('timer.noProject', { defaultValue: 'No project' })}
            {activeTimer.task?.title ? ` · ${activeTimer.task.title}` : ''}
          </span>
        </button>

        {showDropdown && (
          <StartTimerDropdown onClose={() => setShowDropdown(false)} />
        )}
      </div>

      {showCompletionModal && (
        <TimerCompletionModal onClose={closeCompletionModal} />
      )}
    </>
  );
};

export default TimerWidget;
